import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useCategoryTree, useCategoryVersion, useCategoryVersions, useDeleteCategory, useSearchCategories } from '../../hooks';
import { buildAncestorNameMap, buildParentOptions } from '../../lib/categoryTree';
import { formatNumber } from '../../lib/format';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { CategoryFormDialog } from '../components/CategoryFormDialog';
import { CategoryTreeView } from '../components/CategoryTreeView';
import { VersionHistoryDrawer } from '../components/VersionHistoryDrawer';

/** Delete-guard 409 carries `details.childCategories` / `details.products` as string counts. */
function readDeleteConflictCounts(
  details: NormalizedApiError['details'],
): { childCategories?: string; products?: string } | null {
  if (!details || typeof details !== 'object') return null;
  const record = details as Record<string, unknown>;
  const childCategories = typeof record.childCategories === 'string' ? record.childCategories : undefined;
  const products = typeof record.products === 'string' ? record.products : undefined;
  return childCategories === undefined && products === undefined ? null : { childCategories, products };
}

/** Catalog categories — tenant tree (default) with a flat-search fallback while filtering. */
export function CategoriesPage() {
  const { t } = useTranslation('catalog');
  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);

  const treeQuery = useCategoryTree(undefined, { enabled: canView });
  // Doubles as (a) the flat-search fallback while `filter` is set, and (b) a best-effort source of
  // per-category product counts merged onto the tree — `CategoryTreeNode` doesn't carry
  // `productCount` (an OpenSearch-projection concept, not part of the tree read model).
  const searchQuery = useSearchCategories(
    { query: filter.trim() || undefined, page: 0, size: 200, sort: 'name,asc' },
    { enabled: canView },
  );
  const deleteMutation = useDeleteCategory();

  const tree = useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
  const isFiltering = filter.trim() !== '';
  const filteredCategories = useMemo(() => searchQuery.data?.content ?? [], [searchQuery.data]);

  const ancestorNamesById = useMemo(() => buildAncestorNameMap(tree), [tree]);
  const productCountById = useMemo(() => {
    const map = new Map<string, number>();
    for (const category of searchQuery.data?.content ?? []) {
      if (category.productCount !== undefined && category.productCount !== null) {
        map.set(category.id, category.productCount);
      }
    }
    return map;
  }, [searchQuery.data]);
  const parentOptions = useMemo(() => buildParentOptions(tree, editingId), [tree, editingId]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };
  const openEdit = (id: string) => {
    setEditingId(id);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const { id, name } = deleting;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        success(t('categories.delete.success', { name }));
        setDeleting(null);
      },
      onError: (e) => {
        const conflict = e.status === 409 ? readDeleteConflictCounts(e.details) : null;
        if (conflict) {
          notifyError(
            t('categories.delete.errorInUse', {
              childCategories: conflict.childCategories ?? '0',
              products: conflict.products ?? '0',
            }),
          );
        } else {
          notifyError(e.message ?? t('categories.delete.error'));
        }
        setDeleting(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('categories.title')}
      subtitle={t('categories.subtitle')}
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel={t('categories.settingsLabel')}
      search={{ value: filter, onChange: setFilter, placeholder: t('categories.searchPlaceholder') }}
      primaryAction={
        canManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('categories.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {isFiltering ? (
          // ── Flat-search fallback (filtering) ────────────────────────────────
          searchQuery.isLoading ? (
            <LoadingState label={t('categories.loading')} />
          ) : searchQuery.isError ? (
            <ErrorState
              title={t('categories.loadError.title')}
              message={searchQuery.error?.message ?? t('common:error.unknown')}
              onRetry={() => void searchQuery.refetch()}
            />
          ) : filteredCategories.length === 0 ? (
            <EmptyState
              title={t('common:empty.noResults')}
              description={t('categories.empty.filteredDescription')}
              icon={<CategoryRoundedIcon />}
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('categories.table.name')}</TableCell>
                    <TableCell>{t('categories.table.slug')}</TableCell>
                    <TableCell>{t('categories.table.code')}</TableCell>
                    <TableCell align="right">{t('categories.table.productCount')}</TableCell>
                    <TableCell align="right">{t('categories.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.map((category) => {
                    const ancestors = ancestorNamesById.get(category.id) ?? [];
                    return (
                      <TableRow key={category.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {category.name}
                          </Typography>
                          {ancestors.length > 0 ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                              noWrap
                            >
                              {ancestors.join(' / ')}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                          >
                            {category.slug}
                          </Box>
                        </TableCell>
                        <TableCell>{category.code ?? '—'}</TableCell>
                        <TableCell align="right">{formatNumber(category.productCount)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('categories.rowActions.history')}>
                            <IconButton
                              size="small"
                              aria-label={t('categories.rowActions.historyAria', { name: category.name })}
                              onClick={() => setHistoryTarget({ id: category.id, name: category.name })}
                            >
                              <HistoryRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              canManage ? t('categories.rowActions.edit') : t('categories.rowActions.view')
                            }
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={t('categories.rowActions.editAria', { name: category.name })}
                              onClick={() => openEdit(category.id)}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canManage && (
                            <Tooltip title={t('categories.rowActions.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={t('categories.rowActions.deleteAria', { name: category.name })}
                                onClick={() => setDeleting({ id: category.id, name: category.name })}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : // ── Tenant tree (default) ─────────────────────────────────────────────
        treeQuery.isLoading ? (
          <LoadingState label={t('categories.loading')} />
        ) : treeQuery.isError ? (
          <ErrorState
            title={t('categories.loadError.title')}
            message={treeQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void treeQuery.refetch()}
          />
        ) : tree.length === 0 ? (
          <EmptyState
            title={t('categories.empty.title')}
            description={t('categories.empty.description')}
            icon={<CategoryRoundedIcon />}
            action={
              canManage ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('categories.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <CategoryTreeView
            nodes={tree}
            expandedIds={expandedIds}
            onToggle={toggleExpanded}
            ancestorNamesById={ancestorNamesById}
            productCountById={productCountById}
            canManage={canManage}
            onEdit={openEdit}
            onDelete={setDeleting}
            onViewHistory={setHistoryTarget}
          />
        )}
      </TablePageShell>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>{t('categories.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('categories.delete.body', { name: deleting?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryFormDialog
        open={dialogOpen}
        categoryId={editingId}
        parentOptions={parentOptions}
        canManage={canManage}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      {/* ── Version history (user-story-merchant-portal-032) — list + detail only; categories
           have no restore endpoint, so `restore` is omitted entirely. ───────────────────────── */}
      <VersionHistoryDrawer
        open={historyTarget !== null}
        onClose={() => setHistoryTarget(null)}
        subjectLabel={historyTarget?.name ?? ''}
        entityId={historyTarget?.id ?? ''}
        usePage={useCategoryVersions}
        useDetail={useCategoryVersion}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
